import { memo, useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Flex, Spinner, Stack, Table, Text } from '@chakra-ui/react';
import { debounce } from 'lodash';

import { fetchAPI } from '@/api/fetch';
import { apiGetCandidates, GetCandidatesResponse } from '@/api/services/candidate.service';
import { Candidate } from '@/types/Candidate';
import {
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot
} from '@/components/ui/pagination';
import { SearchBox } from '@/components/ui/searchbox';

interface DirectCandidateTableProps {
  candidates: Candidate[];
  selectedCandidateId?: string;
  onSelectCandidate: (candidate: Candidate) => void;
}

const DirectCandidateTable: React.FC<DirectCandidateTableProps> = memo(
  ({ candidates, selectedCandidateId, onSelectCandidate }) => {
    if (candidates.length === 0) {
      return (
        <Table.Row bg="transparent">
          <Table.Cell colSpan={4} textAlign="center" py="32px" fontSize="16px" color="neutrals.500">
            No candidates found
          </Table.Cell>
        </Table.Row>
      );
    }

    return (
      <>
        {candidates.map((candidate) => (
          <Table.Row
            key={candidate.candidate_id}
            bg={candidate.candidate_id === selectedCandidateId ? 'primary.100' : 'transparent'}
            _hover={{ cursor: 'pointer', bg: 'neutrals.50' }}
            onClick={() => onSelectCandidate(candidate)}
            h="42px"
          >
            <Table.Cell fontSize="14px" width="200px">
              {candidate.unique_id}
            </Table.Cell>
            <Table.Cell fontSize="14px" width="250px">
              {candidate.first_name || 'N/A'}
            </Table.Cell>
            <Table.Cell fontSize="14px" width="300px">
              {candidate.email || 'N/A'}
            </Table.Cell>
            <Table.Cell fontSize="14px" width="200px">
              {new Date(candidate.created_at * 1000).toLocaleDateString()}
            </Table.Cell>
          </Table.Row>
        ))}
      </>
    );
  }
);

DirectCandidateTable.displayName = 'DirectCandidateTable';

interface DirectCandidateTabProps {
  selectedCandidateId?: string;
  onSelectCandidate: (candidate: Candidate) => void;
}

export const DirectCandidateTab: React.FC<DirectCandidateTabProps> = memo(
  ({ selectedCandidateId, onSelectCandidate }) => {
    const [directCandidates, setDirectCandidates] = useState<Candidate[]>([]);
    const [directCurrentPage, setDirectCurrentPage] = useState(1);
    const [directTotalPages, setDirectTotalPages] = useState(1);
    const [directKeyword, setDirectKeyword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState<string | null>(null);

    const loadDirectCandidates = useCallback(async (page: number = 1, keyword: string = '') => {
      setIsLoading(true);
      setIsError(null);

      try {
        const response = await fetchAPI<GetCandidatesResponse>({
          request: () =>
            apiGetCandidates({
              params: {
                page,
                limit: 10,
                sort_by: 'created_at',
                sort_order: 'desc',
                keyword: keyword
              }
            })
        });

        if (response.success) {
          setDirectCandidates(response.data.candidates);
          setDirectCurrentPage(response.data.current_page);
          setDirectTotalPages(response.data.total_pages);
        } else {
          console.error('Failed to fetch candidates:', response.message);
          setIsError('Failed to fetch candidates. Please try again later.');
        }
      } catch (error) {
        console.error('Failed to fetch candidates:', error);
        setIsError('Failed to fetch candidates. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    }, []);

    const searchDirectCandidates = useMemo(
      () =>
        debounce(async (searchKeyword: string) => {
          setDirectKeyword(searchKeyword);
          await loadDirectCandidates(1, searchKeyword);
        }, 500),
      [loadDirectCandidates]
    );

    const handleDirectPageChange = useCallback(
      async (page: number) => {
        setDirectCurrentPage(page);
        await loadDirectCandidates(page, directKeyword);
      },
      [loadDirectCandidates, directKeyword]
    );

    useEffect(() => {
      loadDirectCandidates();
    }, [loadDirectCandidates]);

    return (
      <Stack gap="0" mt="24px">
        <Flex justifyContent="space-between" alignItems="center" mb="16px">
          <SearchBox
            placeholder="Search candidate name or email"
            search={searchDirectCandidates}
            disabled={isLoading}
          />
        </Flex>

        <Box overflowX="auto" border="1px solid" borderColor="neutrals.200" borderRadius="8px">
          {isLoading ? (
            <Stack w="100%" h="400px" justifyContent="center">
              <Spinner w="32px" h="32px" color="primary.900" alignSelf="center" />
            </Stack>
          ) : (
            <Table.Root size="sm" variant="line">
              <Table.Header>
                <Table.Row bg="neutrals.100">
                  <Table.ColumnHeader fontSize="14px" fontWeight="bold" width="200px">
                    Candidate ID
                  </Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="14px" fontWeight="bold" width="250px">
                    Name
                  </Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="14px" fontWeight="bold" width="300px">
                    Email
                  </Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="14px" fontWeight="bold" width="200px">
                    Created Date
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                <DirectCandidateTable
                  candidates={directCandidates}
                  selectedCandidateId={selectedCandidateId}
                  onSelectCandidate={onSelectCandidate}
                />
              </Table.Body>
            </Table.Root>
          )}
        </Box>

        {!isLoading && directCandidates.length > 0 && (
          <Flex justifyContent="flex-end" alignItems="center" mt="16px">
            <PaginationRoot
              count={directTotalPages * 10}
              pageSize={10}
              page={directCurrentPage}
              onPageChange={(details) => handleDirectPageChange(details.page)}
            >
              <Stack direction="row" gap="8px">
                <PaginationPrevTrigger />
                <Text fontSize="14px" color="neutrals.700" alignSelf="center">
                  {directCurrentPage} / {directTotalPages}
                </Text>
                <PaginationNextTrigger />
              </Stack>
            </PaginationRoot>
          </Flex>
        )}
        {isError && (
          <Box color="error.500" fontSize="16px" mt="16px" role="alert">
            {isError}
          </Box>
        )}
      </Stack>
    );
  }
);

DirectCandidateTab.displayName = 'DirectCandidateTab';
